import type { QueryClient } from '@tanstack/react-query';
import type { ContributorDto } from '@/types/dtos/contributors/response/contributor.dto';
import { GET_EVENT_CONTRIBUTORS_QUERY_KEY } from '@/state/queries/contributors/use-get-event-contributors';
import { useEventStore } from '@/state/stores/use-event-store';

/**
 * Refetch contributor-facing queries and sync the event store so add UIs can keep a spinner
 * until the new person is actually visible in speakers / hosts / crew lists.
 */
export async function settleContributorAddVisibility(
  queryClient: QueryClient,
  options?: { eventId?: string; extraQueryKeys?: readonly (readonly unknown[])[] },
): Promise<void> {
  // Always include eventId in the key (even if undefined) so we never prefix-match
  // contributor queries for other events and push the wrong list into the event store.
  const contributorKey = [GET_EVENT_CONTRIBUTORS_QUERY_KEY, options?.eventId] as const;

  await Promise.all([
    queryClient.refetchQueries({ queryKey: [...contributorKey], type: 'active' }),
    ...(options?.extraQueryKeys ?? []).map((queryKey) =>
      queryClient.refetchQueries({ queryKey: [...queryKey], type: 'active' }),
    ),
  ]);

  const queries = queryClient.getQueriesData<ContributorDto[]>({
    queryKey: [...contributorKey],
  });

  for (const [, data] of queries) {
    if (Array.isArray(data)) {
      useEventStore.getState().setEventContributors(data);
      break;
    }
  }
}
